import type {
  ActionNodeConfig,
  ConditionNodeConfig,
  WorkflowDefinition,
  WorkflowEdge,
  WorkflowExecution,
  WorkflowEventType,
  WorkflowNode,
  WorkflowStepResult,
  StepStatus,
} from "./workflow-model";
import { createId, validateWorkflow } from "./workflow-model";

export interface WorkflowTestEvent {
  id: string;
  type: WorkflowEventType;
  occurredAt: string;
  data: Record<string, unknown>;
}

export type {
  ExecutionStatus,
  StepStatus,
  WorkflowExecution,
  WorkflowStepResult,
} from "./workflow-model";

export interface WorkflowActionExecutor {
  execute(
    action: ActionNodeConfig,
    event: WorkflowTestEvent,
  ): Promise<{ detail: string }>;
}

export class PreviewActionExecutor implements WorkflowActionExecutor {
  async execute(action: ActionNodeConfig): Promise<{ detail: string }> {
    return Promise.resolve({
      detail: `${action.action.replaceAll("_", " ").toLocaleLowerCase()} → ${action.target}`,
    });
  }
}

export async function runWorkflowPreview(
  workflow: WorkflowDefinition,
  event: WorkflowTestEvent,
  actions: WorkflowActionExecutor = new PreviewActionExecutor(),
): Promise<WorkflowExecution> {
  const issues = validateWorkflow(workflow);
  if (issues.length) throw new Error(issues[0]!.message);
  const startedAt = new Date().toISOString();
  const trigger = workflow.nodes.find(
    (node) => node.config.kind === "TRIGGER",
  )!;
  if (
    trigger.config.kind !== "TRIGGER" ||
    trigger.config.eventType !== event.type
  ) {
    throw new Error("The sample event does not match this workflow trigger.");
  }
  const steps: WorkflowStepResult[] = [];
  const queue: WorkflowNode[] = [trigger];
  const visited = new Set<string>();
  let failed = false;

  while (queue.length && !failed) {
    const node = queue.shift()!;
    if (visited.has(node.id)) continue;
    visited.add(node.id);
    try {
      const outcome = await executeNode(node, event, actions);
      steps.push({
        nodeId: node.id,
        label: node.label,
        status: outcome.status,
        detail: outcome.detail,
        completedAt: new Date().toISOString(),
      });
      queue.push(
        ...nextNodes(node, outcome.branch, workflow.nodes, workflow.edges),
      );
    } catch (error) {
      failed = true;
      steps.push({
        nodeId: node.id,
        label: node.label,
        status: "FAILED",
        detail: error instanceof Error ? error.message : "Step failed.",
        completedAt: new Date().toISOString(),
      });
    }
  }
  const completedAt = new Date().toISOString();
  return {
    id: createId("run"),
    workflowId: workflow.id,
    workflowName: workflow.name,
    workflowVersion: workflow.version,
    triggerEventId: event.id,
    triggerEventType: event.type,
    status: failed ? "FAILED" : "SUCCEEDED",
    test: true,
    startedAt,
    completedAt,
    steps,
  };
}

export function sampleEvent(eventType: WorkflowEventType): WorkflowTestEvent {
  return {
    id: createId("event"),
    type: eventType,
    occurredAt: new Date().toISOString(),
    data: {
      businessId: "81000000-0000-4000-8000-000000000001",
      fromStatus: "IN_PROGRESS",
      toStatus: eventType === "job.status_changed.v1" ? "COMPLETED" : "ACTIVE",
      jobId: "84000000-0000-4000-8000-000000000001",
      customerId: "82000000-0000-4000-8000-000000000001",
      totalAmountCents: 42500,
    },
  };
}

async function executeNode(
  node: WorkflowNode,
  event: WorkflowTestEvent,
  actions: WorkflowActionExecutor,
): Promise<{
  status: StepStatus;
  detail: string;
  branch?: WorkflowEdge["branch"];
}> {
  switch (node.config.kind) {
    case "TRIGGER":
      return {
        status: "SUCCEEDED",
        detail: `Received ${node.config.eventType}`,
      };
    case "CONDITION": {
      const matched = evaluateCondition(node.config, event);
      return {
        status: "SUCCEEDED",
        detail: matched ? "Condition matched" : "Condition did not match",
        branch: matched ? "TRUE" : "FALSE",
      };
    }
    case "DELAY":
      return {
        status: "SUCCEEDED",
        detail: `Preview skipped a ${node.config.amount} ${node.config.unit.toLocaleLowerCase()} delay`,
      };
    case "ACTION": {
      const result = await actions.execute(node.config, event);
      return { status: "SUCCEEDED", detail: result.detail };
    }
  }
}

function evaluateCondition(
  condition: ConditionNodeConfig,
  event: WorkflowTestEvent,
): boolean {
  const actual = readPath(
    event as unknown as Record<string, unknown>,
    condition.field,
  );
  const expected = condition.value;
  switch (condition.operator) {
    case "EXISTS":
      return actual !== undefined && actual !== null;
    case "EQUALS":
      return String(actual ?? "") === expected;
    case "NOT_EQUALS":
      return String(actual ?? "") !== expected;
    case "CONTAINS":
      return String(actual ?? "")
        .toLocaleLowerCase()
        .includes(expected.toLocaleLowerCase());
  }
}

function readPath(value: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => {
    if (
      typeof current !== "object" ||
      current === null ||
      Array.isArray(current)
    ) {
      return undefined;
    }
    return (current as Record<string, unknown>)[key];
  }, value);
}

function nextNodes(
  current: WorkflowNode,
  branch: WorkflowEdge["branch"],
  nodes: readonly WorkflowNode[],
  edges: readonly WorkflowEdge[],
): WorkflowNode[] {
  return edges
    .filter(
      (edge) =>
        edge.source === current.id &&
        (current.config.kind !== "CONDITION" || edge.branch === branch),
    )
    .map((edge) => nodes.find((node) => node.id === edge.target))
    .filter((node): node is WorkflowNode => Boolean(node));
}
